import { useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import ReceiptModal from '@/components/feature/ReceiptModal';
import { useOrders } from '@/hooks/useOrders';
import { confirmOrderAsSale, updateOrderStatus, markOrderAsPaid, retryOrderSale } from '@/services/orders';
import type { Order } from '@/services/orders';
import { METHOD_LABELS } from '@/services/payments';
import { downloadCsv } from '@/lib/csv';
import { useAuth } from '@/context/AuthContext';
import { isOperationsRole } from '@/lib/rbac';
import OrderModal from './components/OrderModal';
import OrderDetailModal from './components/OrderDetailModal';
import { downloadShippingLabel } from '@/lib/shippingLabel';
import { useBrandIdentity } from '@/hooks/useBrandIdentity';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  received: { label: 'Recibido', color: 'text-blue-600', bg: 'bg-blue-50' },
  pending_payment: { label: 'Pendiente de Pago', color: 'text-amber-600', bg: 'bg-amber-50' },
  payment_initiated: { label: 'Pago Iniciado', color: 'text-amber-600', bg: 'bg-amber-50' },
  payment_confirmed: { label: 'Pago Confirmado', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  listo_envio: { label: 'Pendiente de envío', color: 'text-emerald-700', bg: 'bg-emerald-100' },
  preparing: { label: 'Preparando', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  packed: { label: 'Empacado', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  shipped: { label: 'Despachado', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  in_transit: { label: 'En Tránsito', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  delivered: { label: 'Entregado', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  closed: { label: 'Cerrado', color: 'text-foreground-600', bg: 'bg-background-200' },
  cancelled: { label: 'Cancelado', color: 'text-red-600', bg: 'bg-red-50' },
};

const STATUSES = ['all', 'received', 'pending_payment', 'payment_initiated', 'financing', 'payment_confirmed', 'listo_envio', 'preparing', 'shipped', 'delivered', 'closed', 'cancelled'];
const STATUS_LABELS: Record<string, string> = {
  all: 'Todos',
  received: 'Recibidos',
  pending_payment: 'Pend. Pago',
  payment_initiated: 'Pago Iniciado',
  financing: 'En financiamiento',
  payment_confirmed: 'Pago Confirmado',
  listo_envio: 'Pend. Envío',
  preparing: 'Preparando',
  shipped: 'Despachado',
  delivered: 'Entregado',
  closed: 'Cerrado',
  cancelled: 'Cancelado',
};

// Un pedido financiado con Sistecrédito que aún no ha sido aprobado por el
// aliado se muestra como "En financiamiento" (la venta todavía no se firma).
// Los rechazados/cancelados por el aliado se auto-cancelan y NO cuentan aquí.
const isFinancing = (o: Order) =>
  o.paymentMethod === 'sistecredito' &&
  !o.financingRejected &&
  o.paymentStatus !== 'approved' &&
  o.status !== 'cancelled' &&
  o.status !== 'closed';

export default function OrdersPage() {
  const { profile } = useAuth();
  const isOperations = isOperationsRole(profile?.role ?? '');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { data: orders, loading, error, refetch } = useOrders();
  const [showNew, setShowNew] = useState(false);
  const [actionError, setActionError] = useState('');

  // Estado para marcar como pagado
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const [receiptOrder, setReceiptOrder] = useState<Order | null>(null);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const identity = useBrandIdentity();

  // Enlace directo desde Reservas activas de Inventario: abre el detalle del
  // pedido indicado en ?order= y limpia el parámetro para no reabrirlo.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusOrderId = searchParams.get('order');
  useEffect(() => {
    if (!focusOrderId || loading) return;
    const found = orders.find((o) => o.id === focusOrderId);
    if (found) setDetailOrder(found);
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
  }, [focusOrderId, loading, orders, searchParams, setSearchParams]);

  async function handleMarkAsPaid(orderId: string) {
    setActionError('');
    setMarkingPaid(orderId);
    const { error: markErr } = await markOrderAsPaid(orderId);
    setMarkingPaid(null);
    if (markErr) {
      setActionError(markErr);
    } else {
      setConfirmSuccess('Pago marcado como recibido correctamente.');
      refetch();
    }
  }

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    const { error: updateError } = await updateOrderStatus(id, status);
    if (updateError) setActionError(updateError);
    refetch();
  }

  // Estado del modal de confirmación de venta.
  const [confirmTarget, setConfirmTarget] = useState<Order | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactionRef, setTransactionRef] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [confirmSuccess, setConfirmSuccess] = useState('');
  // Modo reintento: el pedido venía cancelado por rechazo de Sistecrédito y se
  // reabre/reconfirma con otro método de pago al confirmar.
  const [retryMode, setRetryMode] = useState(false);

  const confirmable = (o: Order) =>
    o.status === 'received' || o.status === 'pending_payment' || o.status === 'payment_confirmed';

  function exportOrders() {
    downloadCsv(
      'pedidos',
      ['Pedido', 'Cliente', 'Canal', 'Total', 'Estado', 'Fecha'],
      orders.map((o) => [
        o.orderNumber,
        o.customerName,
        o.channel,
        o.total,
        o.status,
        o.createdAt ? new Date(o.createdAt).toLocaleDateString('es-CO') : '',
      ]),
    );
  }

  async function handleConfirmSale(e: FormEvent) {
    e.preventDefault();
    if (!confirmTarget) return;
    setConfirming(true);
    setConfirmError('');
    setConfirmSuccess('');

    const res = retryMode
      ? await retryOrderSale(confirmTarget.id, paymentMethod, transactionRef.trim() || undefined)
      : await confirmOrderAsSale(confirmTarget.id, paymentMethod, transactionRef.trim() || undefined);

    if (res.success) {
      if (res.saleNumber) {
        setConfirmSuccess(
          `${retryMode ? 'Reintento confirmado' : 'Venta'} ${res.saleNumber} confirmada correctamente.`,
        );
      } else {
        setConfirmSuccess(
          retryMode
            ? 'Reintento confirmado. El pedido quedó listo para producción.'
            : 'Venta confirmada correctamente.',
        );
      }
      setConfirmTarget(null);
      setRetryMode(false);
      setTransactionRef('');
      await refetch();
    } else {
      setConfirmError(res.error || 'No se pudo confirmar la venta.');
    }
    setConfirming(false);
  }

  const regularOrders = orders.filter((o) => !o.production);

  const filtered = regularOrders.filter((o) => {
    if (filter === 'financing') {
      if (!isFinancing(o)) return false;
    } else if (filter !== 'all' && o.status !== filter) {
      return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!o.orderNumber.toLowerCase().includes(q) && !o.customerName.toLowerCase().includes(q)) {
        return false;
      }
    }
    if (o.createdAt) {
      const d = new Date(o.createdAt);
      if (dateFrom && d < new Date(`${dateFrom}T00:00:00`)) return false;
      if (dateTo && d > new Date(`${dateTo}T23:59:59`)) return false;
    }
    return true;
  });

  const counts: Record<string, number> = {};
  STATUSES.forEach((s) => {
    counts[s] =
      s === 'all'
        ? regularOrders.length
        : s === 'financing'
          ? regularOrders.filter(isFinancing).length
          : regularOrders.filter((o) => o.status === s).length;
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Pedidos</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{regularOrders.length} pedidos registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportOrders}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-line mr-1.5"></i>Exportar
            </button>
            {!isOperations && (
              <button
                onClick={() => setShowNew(true)}
                className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                <i className="ri-add-line mr-1.5"></i>Nuevo Pedido
              </button>
            )}
          </div>
        </div>

        {confirmSuccess && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
            <p className="text-sm text-emerald-800 font-medium">{confirmSuccess}</p>
            <button
              onClick={() => setConfirmSuccess('')}
              className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === s
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {STATUS_LABELS[s]} ({counts[s] || 0})
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border border-background-200 rounded-lg px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border border-background-200 rounded-lg px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-xs px-3 py-1.5 text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-1"></i>Limpiar
              </button>
            )}
            <div className="relative max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="Buscar pedido o cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Pedido</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Productos</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Canal</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Total</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Medio de pago</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const s = STATUS_MAP[order.status] || {
                    label: order.status,
                    color: 'text-foreground-500',
                    bg: 'bg-background-100',
                  };
                  return (
                    <tr key={order.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-accent-600 font-medium whitespace-nowrap">{order.orderNumber}</span>
                          {order.hasShipping && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
                              Con envío
                            </span>
                          )}
                          <button
                            onClick={() => setDetailOrder(order)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                            title="Ver detalle"
                            aria-label="Ver detalle del pedido"
                          >
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setReceiptOrder(order)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                            title="Descargar comprobante"
                            aria-label="Descargar comprobante"
                          >
                            <i className="ri-download-2-line text-sm"></i>
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              downloadShippingLabel({
                                companyName: identity.companyName,
                                companyPhone: identity.companyPhone,
                                companyCity: identity.companyCity,
                                companyLogo: identity.logo,
                                senderName: identity.senderName,
                                senderId: identity.senderId,
                                senderPhone: identity.senderPhone,
                                senderAddress: identity.senderAddress,
                                senderCity: identity.senderCity,
                                recipientName: order.recipientName || order.customerName,
                                recipientPhone: order.recipientPhone || order.customerPhone,
                                recipientId: order.recipientIdNumber,
                                recipientEmail: order.recipientEmail || order.customerEmail,
                                recipientCity: order.shippingCity || order.customerCity,
                                recipientDepartment: order.shippingDepartment,
                                recipientAddress: order.shippingAddress,
                                recipientNotes: order.notes,
                                orderNumber: order.orderNumber,
                                orderDate: order.createdAt,
                                contents: order.items.map(it => `${it.productName} · ${it.sizeLabel} × ${it.quantity}`).join(', '),
                                paymentMode: order.paymentMethod === 'cash' ? 'Cobro Contraentrega' : 'Flete Pagado',
                                total: order.paymentMethod === 'cash' ? order.total : 0,
                              });
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                            title="Descargar Guía de Envío (Media Carta)"
                            aria-label="Descargar guía de envío"
                          >
                            <i className="ri-file-paper-2-line text-sm"></i>
                          </button>
                        </div>
                        {order.siblingOrderNumber && (
                          <Link
                            to="/admin/produccion"
                            className="mt-0.5 block text-xs text-amber-600 hover:text-amber-700 font-medium whitespace-nowrap"
                          >
                            <i className="ri-links-line mr-0.5"></i>Grupo: {order.siblingOrderNumber}
                          </Link>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{order.customerName || '—'}</p>
                          {order.customerCity && (
                            <p className="text-xs text-foreground-500">{order.customerCity}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <div className="space-y-0.5">
                          {order.items.slice(0, 2).map((item) => (
                            <p key={item.id} className="text-xs text-foreground-700">
                              {item.productName} — {item.sizeLabel} × {item.quantity}
                            </p>
                          ))}
                          {order.items.length > 2 && (
                            <p className="text-xs text-foreground-400">+{order.items.length - 2} más</p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 capitalize">
                          {order.channel === 'ecommerce' ? 'E-commerce' : order.channel || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-foreground-900 font-medium whitespace-nowrap">
                        ${order.total.toLocaleString()}
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                          {order.paymentMethod === 'cash'
                          ? 'Contra entrega'
                          : METHOD_LABELS[order.paymentMethod] || order.paymentMethod || '—'}
                        </span>
                        {order.paymentStatus === 'approved' && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                            Pagado
                          </span>
                        )}
                        {order.financingRejected && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                            Rechazado
                          </span>
                        )}
                        {order.paymentStatus === 'pending' && order.paymentMethod && !order.financingRejected && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            Pendiente
                          </span>
                        )}
                        {!isOperations && order.paymentStatus === 'pending' && order.paymentMethod && !order.financingRejected && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleMarkAsPaid(order.id)}
                            disabled={markingPaid === order.id}
                            className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
                          >
                            {markingPaid === order.id ? (
                              <><i className="ri-loader-4-line animate-spin"></i>Marcando...</>
                            ) : (
                              <><i className="ri-checkbox-circle-line"></i>Marcar como pagado</>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {isFinancing(order) && (
                          <span className="inline-flex items-center gap-1 mb-1 text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium whitespace-nowrap">
                            <i className="ri-hand-coin-line"></i>En financiamiento
                          </span>
                        )}
                        {order.financingRejected && (
                          <span className="inline-flex items-center gap-1 mb-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium whitespace-nowrap">
                            <i className="ri-close-circle-line"></i>Financiamiento rechazado
                          </span>
                        )}
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${s.color} ${s.bg}`}
                        >
                          {Object.keys(STATUS_MAP).map((key) => (
                            <option key={key} value={key}>{STATUS_MAP[key].label}</option>
                          ))}
                        </select>
                        {!isOperations && order.status === 'listo_envio' && (
                          <span className="block mt-1.5 text-xs text-emerald-600 font-medium whitespace-nowrap">
                            <i className="ri-checkbox-circle-line mr-1"></i>Pagado
                          </span>
                        )}
                        {!isOperations && confirmable(order) && (
                          <button
                            onClick={() => {
                              setConfirmError('');
                              setConfirmSuccess('');
                              setRetryMode(false);
                              setConfirmTarget(order);
                            }}
                            className="block mt-1.5 text-xs text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-check-double-line mr-1"></i>Confirmar Venta
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-xs text-foreground-500 whitespace-nowrap">
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleDateString('es-CO', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">No se encontraron pedidos</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando pedidos...</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de venta */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !confirming && setConfirmTarget(null)}></div>
          <div className="relative w-full max-w-md bg-white rounded-xl border border-background-200 p-6 shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-foreground-950">
                  {retryMode ? 'Reintentar con otro método' : 'Confirmar Venta'}
                </h2>
                <p className="text-sm text-foreground-500 mt-0.5">
                  {confirmTarget.orderNumber} · ${confirmTarget.total.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => !confirming && setConfirmTarget(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleConfirmSale} className="space-y-4">
              {retryMode && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700 leading-relaxed">
                    <i className="ri-refresh-line mr-1"></i>
                    Este pedido se había <strong>cancelado por rechazo de Sistecrédito</strong>. Al confirmar, el sistema
                    lo reabre, vuelve a reservar el stock, registra el pago con el nuevo método y firma la venta.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Método de pago</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full text-sm border border-background-200 rounded-lg px-3 py-2.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
                >
                  <option value="cash">Contra entrega (efectivo)</option>
                  <option value="nequi_negocios">Nequi Negocios</option>
                  <option value="transfer">Transferencia</option>
                  <option value="pse">PSE</option>
                  <option value="card_credit">Tarjeta de crédito</option>
                  <option value="card_debit">Tarjeta débito</option>
                  <option value="sistecredito">Sistecrédito (compra a cuotas)</option>
                  <option value="payment_link">Link de pago</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Referencia de transacción <span className="text-foreground-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="Ej: 123456789"
                  className="w-full text-sm border border-background-200 rounded-lg px-3 py-2.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
                />
              </div>

              {paymentMethod === 'sistecredito' && (
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                  <p className="text-xs text-secondary-900">
                    <i className="ri-hand-coin-line mr-1"></i>
                    Confirma solo si <strong>Sistecrédito ya aprobó</strong> el financiamiento del cliente. Al confirmar
                    se firma la venta y se descuenta el inventario de inmediato (igual que un pago aprobado automático).
                  </p>
                </div>
              )}

              <div className="bg-accent-50 border border-accent-200 rounded-lg p-3">
                <p className="text-xs text-accent-800">
                  Al confirmar, se genera la venta, se descuenta el inventario (con trazabilidad), se registra la
                  comisión y el pago, y el pedido pasa a <strong>Cerrado</strong>.
                </p>
              </div>

              {confirmError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <p className="text-sm text-red-700">{confirmError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => !confirming && setConfirmTarget(null)}
                  className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={confirming}
                  className="flex-1 text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {confirming ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-1.5"></i>Confirmando...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-double-line mr-1.5"></i>Confirmar Venta
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNew && <OrderModal onClose={() => setShowNew(false)} onSaved={refetch} />}

      {detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onRetryWithMethod={(o) => {
            setDetailOrder(null);
            setConfirmError('');
            setConfirmSuccess('');
            setPaymentMethod('cash');
            setTransactionRef('');
            setRetryMode(true);
            setConfirmTarget(o);
          }}
        />
      )}

      {receiptOrder && (
        <ReceiptModal
          receipt={{
            title: 'Comprobante de Pedido',
            number: receiptOrder.orderNumber,
            date: receiptOrder.createdAt,
            customerName: receiptOrder.customerName,
            customerPhone: receiptOrder.customerPhone,
            customerCity: receiptOrder.customerCity,
            paymentMethod: receiptOrder.paymentMethod,
            paymentStatus: receiptOrder.paymentStatus,
            items: receiptOrder.items.map((it) => ({
              productName: it.productName,
              sizeLabel: it.sizeLabel,
              quantity: it.quantity,
              unitPrice: it.unitPrice,
              subtotal: it.quantity * it.unitPrice,
            })),
            total: receiptOrder.total,
            notes: receiptOrder.notes,
          }}
          onClose={() => setReceiptOrder(null)}
        />
      )}
    </AdminLayout>
  );
}